import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Empty, Progress, Typography } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { api } from '../api'
import { DocCompareViewer } from '../components/DocCompareViewer'
import { PageLoading } from '../components/ui'
import './DocCompareResult.css'

const { Text } = Typography
const POLL_MS = 2000

type SessionStatus = {
  id: number
  status: string
  progress: number
  status_message: string
  left_title: string
  right_title: string
  error_message?: string | null
}

type SessionOut = SessionStatus & {
  compare_mode: string
  result: Record<string, unknown> | null
  expires_at: string | null
}

export default function DocCompareResult() {
  const { sessionId } = useParams()
  const [session, setSession] = useState<SessionOut | null>(null)
  const [bootLoading, setBootLoading] = useState(true)
  const [resultLoading, setResultLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = useCallback(async (id: number) => {
    const r = await api.get<SessionStatus>(`/public/doc-compare/sessions/${id}/status`)
    return r.data
  }, [])

  const fetchFullSession = useCallback(async (id: number) => {
    const r = await api.get<SessionOut>(`/public/doc-compare/sessions/${id}`)
    setSession(r.data)
    return r.data
  }, [])

  useEffect(() => {
    const id = Number(sessionId)
    if (!Number.isFinite(id) || id <= 0) {
      setError('无效的比对记录')
      setBootLoading(false)
      return
    }

    let cancelled = false
    let timer: number | undefined

    const poll = async () => {
      try {
        const st = await fetchStatus(id)
        if (cancelled) return
        setError(null)
        setSession((prev) => ({
          ...(prev || ({} as SessionOut)),
          ...st,
          compare_mode: prev?.compare_mode || 'pdf',
          result: prev?.result ?? null,
          expires_at: prev?.expires_at ?? null,
        }))
        setBootLoading(false)

        if (st.status === 'completed') {
          setResultLoading(true)
          await fetchFullSession(id)
          setResultLoading(false)
          return
        }
        if (st.status === 'error') {
          return
        }
        timer = window.setTimeout(poll, POLL_MS)
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : '加载失败')
        setBootLoading(false)
        setResultLoading(false)
      }
    }

    void poll()

    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [sessionId, fetchStatus, fetchFullSession])

  const exportDiff = () => {
    if (!session?.id) return
    window.open(`/api/public/doc-compare/sessions/${session.id}/export?format=html`, '_blank')
  }

  const closeWindow = () => {
    window.close()
  }

  const shell = (body: ReactNode, titles?: { left: string; right: string }) => (
    <div className="doc-compare-result-standalone">
      <header className="doc-compare-result-topbar">
        <div className="doc-compare-result-topbar-main">
          <Text strong className="doc-compare-result-topbar-title">
            文档比对结果
          </Text>
          {titles ? (
            <Text type="secondary" className="doc-compare-result-topbar-desc" ellipsis>
              {titles.left} · {titles.right}
            </Text>
          ) : null}
        </div>
        <Button type="text" icon={<CloseOutlined />} onClick={closeWindow}>
          关闭
        </Button>
      </header>
      <main className="doc-compare-result-body">{body}</main>
    </div>
  )

  if (bootLoading && !session) {
    return (
      <div className="doc-compare-result-standalone">
        <PageLoading tip="正在打开比对…" />
      </div>
    )
  }

  if (error) {
    return shell(
      <div className="doc-compare-result-empty">
        <Empty description={error} />
      </div>,
    )
  }

  if (!session) {
    return shell(
      <div className="doc-compare-result-empty">
        <Empty description="未找到比对记录" />
      </div>,
    )
  }

  const titles = { left: session.left_title, right: session.right_title }

  if (session.status === 'pending' || session.status === 'running') {
    return shell(
      <div className="doc-compare-result-progress">
        <Progress type="circle" percent={session.progress} status="active" size={120} />
        <Text strong style={{ marginTop: 20, fontSize: 16 }}>
          {session.status_message || '正在比对…'}
        </Text>
        <Text type="secondary" style={{ marginTop: 8 }}>
          逐页 OCR 识别与比对，大文件可能需要数分钟；进度会随页数更新
        </Text>
      </div>,
      titles,
    )
  }

  if (session.status === 'error') {
    return shell(
      <div className="doc-compare-result-empty">
        <Empty description={session.error_message || '比对失败'} />
      </div>,
      titles,
    )
  }

  if (resultLoading || !session.result) {
    return shell(
      <div className="doc-compare-result-progress">
        <PageLoading tip="比对已完成，正在加载结果…" />
      </div>,
      titles,
    )
  }

  return shell(
    <DocCompareViewer
      leftTitle={session.left_title}
      rightTitle={session.right_title}
      result={session.result as never}
      expiresAt={session.expires_at}
      onExportDiff={exportDiff}
    />,
    titles,
  )
}
